/**
 * Simple event emitter for application-wide events
 */
export type EventCallback<T = any> = (data: T) => void;

class EventEmitter {
  private events: Map<string, EventCallback[]> = new Map();

  /**
   * Subscribe to an event
   * @param event Event name
   * @param callback Function to call when event is emitted
   * @returns Unsubscribe function
   */
  subscribe<T>(event: string, callback: EventCallback<T>): () => void {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }

    const callbacks = this.events.get(event)!;
    callbacks.push(callback as EventCallback);

    // Return unsubscribe function
    return () => {
      const index = callbacks.indexOf(callback as EventCallback);
      if (index !== -1) {
        callbacks.splice(index, 1);
      }
    };
  }

  /**
   * Emit an event with data
   * @param event Event name
   * @param data Data to pass to listeners
   */
  emit<T>(event: string, data: T): void {
    if (!this.events.has(event)) {
      return;
    }

    const callbacks = this.events.get(event)!;
    callbacks.forEach(callback => callback(data));
  }
}

// Export a singleton instance
export const eventEmitter = new EventEmitter();

// Define event names as constants
export const EVENTS = {
  LOCATION_UPDATED: 'location_updated',
  OBJECT_CREATED: 'object_created',
  OBJECTS_LOADED: 'objects_loaded',
};
